// src/modules/components/Footer/styles.js
// VEGA ROBOTICS - Footer Styles (Alinhado com Header)
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// ============================================================
// DESIGN TOKENS
// ============================================================
const colors = {
  bgDark: '#111111',
  bgDarker: '#0a0a0a',
  textWhite: '#FFFFFF',
  textMuted: 'rgba(255, 255, 255, 0.6)',
  textMutedHover: 'rgba(255, 255, 255, 0.9)',
  border: 'rgba(255, 255, 255, 0.1)',
  vegaRed: '#9D0008',
  accent: '#9D0008',
};

// ============================================================
// FOOTER CONTAINER
// ============================================================
export const FooterContainer = styled.footer`
  position: relative;
  background: ${colors.bgDark};
  color: ${colors.textWhite};
  padding-top: 64px;
  
  @media (max-width: 768px) {
    padding-top: 48px;
  }
`;

export const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 48px;
  
  @media (max-width: 1024px) {
    padding: 0 24px;
  }
  
  @media (max-width: 640px) {
    padding: 0 16px;
  }
`;

// ============================================================
// FOOTER MAIN
// ============================================================
export const FooterMain = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 2fr auto;
  gap: 64px;
  padding-bottom: 48px;
  border-bottom: 1px solid ${colors.border};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

// ============================================================
// BRAND SECTION
// ============================================================
export const FooterBrand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Logo = styled(Link)`
  display: inline-flex;

  img {
    height: 38px;
    filter: brightness(0) invert(1);
    transition: opacity 200ms ease;

    @media (max-width: 640px) {
      height: 32px;
    }
  }

  &:hover img {
    opacity: 0.8;
  }
`;

export const LogoExternal = styled.a`
  display: inline-flex;

  img {
    height: 38px;
    filter: brightness(0) invert(1);
    transition: opacity 200ms ease;

    @media (max-width: 640px) {
      height: 32px;
    }
  }

  &:hover img {
    opacity: 0.8;
  }
`;

export const BrandDescription = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.textMuted};
  max-width: 300px;
`;

export const NvidiaInceptionBadge = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: ${colors.vegaRed};
  color: ${colors.textWhite};
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 4px;
  width: fit-content;
  transition: all 200ms ease;
  
  &:hover {
    background: #b30009;
    transform: translateY(-1px);
  }
`;

// ============================================================
// LINKS SECTION
// ============================================================
export const FooterLinks = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 48px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const FooterLinkGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const GroupTitle = styled.h4`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${colors.textMuted};
  margin-bottom: 4px;

  @media (max-width: 640px) {
    font-size: 13px;
  }
`;

export const FooterLink = styled(Link)`
  font-size: 14px;
  color: ${colors.textMuted};
  text-decoration: none;
  transition: color 200ms ease;
  
  &:hover {
    color: ${colors.textWhite};
  }
`;

export const FooterExternalLink = styled.a`
  font-size: 14px;
  color: ${colors.textMuted};
  text-decoration: none;
  transition: color 200ms ease;
  
  &:hover {
    color: ${colors.textWhite};
  }
`;

// ============================================================
// CONTACT SECTION
// ============================================================
export const FooterContact = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  
  @media (max-width: 1024px) {
    grid-column: span 2;
    align-items: flex-start;
  }
  
  @media (max-width: 640px) {
    grid-column: span 1;
  }
`;

export const ContactItem = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  color: ${colors.textMuted};
  text-decoration: none;
  transition: color 200ms ease;
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
    font-size: 16px;
  }
  
  span {
    line-height: 1.5;
  }
  
  &:hover {
    color: ${colors.textWhite};
  }
`;

// ============================================================
// SOCIAL LINKS
// ============================================================
export const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
`;

export const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: ${colors.textMuted};
  font-size: 20px;
  transition: all 200ms ease;

  @media (hover: hover) {
    &:hover {
      background: ${colors.vegaRed};
      color: ${colors.textWhite};
      transform: translateY(-2px);
    }
  }

  &:active {
    background: ${colors.vegaRed};
    color: ${colors.textWhite};
    transform: scale(0.95);
  }
`;

// ============================================================
// FOOTER BOTTOM
// ============================================================
export const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 0;
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
`;

export const Copyright = styled.p`
  font-size: 13px;
  color: ${colors.textMuted};
`;

export const LegalLinks = styled.div`
  display: flex;
  gap: 24px;
`;

export const LegalLink = styled(Link)`
  font-size: 13px;
  color: ${colors.textMuted};
  text-decoration: none;
  transition: color 200ms ease;
  
  &:hover {
    color: ${colors.textWhite};
  }
`;

// ============================================================
// BACK TO TOP
// ============================================================
export const BackToTop = styled.button`
  position: absolute;
  right: 48px;
  bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${colors.border};
  border-radius: 50%;
  color: ${colors.textMuted};
  font-size: 18px;
  cursor: pointer;
  transition: all 200ms ease;
  
  &:hover {
    background: ${colors.vegaRed};
    border-color: ${colors.vegaRed};
    color: ${colors.textWhite};
    transform: translateY(-2px);
  }
  
  @media (max-width: 1024px) {
    right: 24px;
  }
  
  @media (max-width: 640px) {
    right: 16px;
    bottom: 80px;
  }
`;